export interface RouteConfig {
  path: string
  title: string
  requiresAuth: boolean
  requiredRoles?: string[]
  requiredLicense?: string[]
}

export const ROUTES = {
  // Auth routes
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  VERIFY_EMAIL: '/auth/verify-email',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  // Plan 4 Task 9 — Keycloak's link-follow UX lands here after the email send.
  // The user clicks the link in the email and Keycloak's hosted "Update
  // password" screen takes over (outside the SPA).
  PASSWORD_RESET_SENT: '/auth/password-reset-sent',
  SIGNUP: '/signup',
  SIGNUP_SUCCESS: '/signup/success',
  CHECKOUT: '/checkout',

  // Main routes
  HOME: '/',
  MAP: '/map',
  MAP_VIEW: '/map/:uuid',
  LIBRARY: '/library',
  LIBRARY_ITEM: '/library/:type/:id',
  PROFILE: '/profile',

  // Forms routes
  FORMS: '/forms',
  FORMS_BUILDER: '/forms/builder/:id',
  FORMS_VIEW: '/forms/view/:id',
  FORMS_SUBMISSIONS: '/forms/:id/submissions',

  // Dashboard routes
  DASHBOARD: '/dashboard',
  DASHBOARD_BUILDER: '/dashboard/builder/:id',
  DASHBOARD_VIEW: '/dashboard/view/:id',

  // Settings routes
  SETTINGS: '/settings',
  SETTINGS_BRANDING: '/settings/branding',
  SETTINGS_LICENSE: '/settings/license',
  SETTINGS_UTILITIES: '/settings/utilities',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_SERVERS: '/settings/servers',
  SETTINGS_GROUPS: '/settings/groups',
  SETTINGS_PLANNING: '/settings/planning',

  // Feature routes
  GEOCODING: '/geocoding',
  GEOCODING_SESSIONS: '/geocoding/sessions',
  GEOCODING_SESSION: '/geocoding/sessions/:id',
  SCRIPTS: '/scripts',
  SCRIPT: '/scripts/:id',
  // Monitoring — plan2-usage-panels Task 23. No LicenseGuard: 'monitoring' is
  // absent from CLOUD_SUBSCRIPTION_FEATURE_MODULES, which is exactly why the
  // backend's read-auth.ts deliberately omits requireModule (see the route
  // registration in routes/index.tsx for the full rationale).
  MONITORING: '/monitoring',
  MONITORING_SITE: '/monitoring/:siteId',

  // Capacity — same gating rationale as Monitoring: no LicenseGuard, because
  // 'capacity' is not an entitlement module either. The backend gates these
  // routes on the CALLER being a central operator
  // (require-central-caller.ts), so a tenant admin gets a 403 from the API
  // rather than an empty page.
  CAPACITY: '/capacity',
  CAPACITY_RUN: '/capacity/:runId',

  // Planning (Kebenaran Merancang): licensed module 'planning', every path behind
  // LicenseGuard in planning-routes.tsx. Not ROUTES.REGISTER (/auth/register).
  PLANNING: '/planning',
  PLANNING_SUBMISSIONS: '/planning/submissions',
  PLANNING_SUBMISSION_NEW: '/planning/submissions/new',
  PLANNING_SUBMISSION: '/planning/submissions/:id',
  PLANNING_REGISTRATIONS: '/planning/registrations',
  PLANNING_ACTIVITY: '/planning/activity',
  PLANNING_REGISTER_EXPORTS: '/planning/register-exports',
  // Public, OUTSIDE AuthGuard (planningPublicRoutes in planning-routes.tsx): no session, and the
  // API host picks the tenant. Not ROUTES.REGISTER either (/auth/register redirects to /signup).
  PLANNING_REGISTER: '/planning/register',
  PLANNING_REGISTER_STATUS: '/planning/register/status',

  // Security routes
  SECURITY: '/security',
  SECURITY_SCANS: '/security/scans',
  SECURITY_AGENTS: '/security/agents',

  // Hidden, flag-gated (?robotdebug=1) realtime-channel verification surface.
  ROBOT_CHANNEL_DEBUG: '/debug/robot-channel',

  // Admin routes (kept for redirect; remove in follow-up after analytics confirm zero /admin/* traffic)
  ADMIN_TENANTS: '/admin/tenants',
  ADMIN_AUDIT_LOGS: '/admin/audit-logs',

  // Settings routes (moved from /admin/*)
  SETTINGS_TENANTS: '/settings/tenants',
  SETTINGS_AUDIT_LOGS: '/settings/audit-logs',
  SETTINGS_JOBS: '/settings/jobs',

  // Account status routes (outside AuthGuard)
  ACCOUNT_SUSPENDED: '/account-suspended',
  ACCOUNT_DELETED: '/account-deleted',
  // Issue #259 — landing on a subdomain that doesn't map to any tenant.
  ACCOUNT_NOT_FOUND: '/account-not-found',
} as const

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES]

export const routeConfigs: Record<string, RouteConfig> = {
  [ROUTES.LOGIN]: {
    path: ROUTES.LOGIN,
    title: 'Login',
    requiresAuth: false,
  },
  [ROUTES.REGISTER]: {
    path: ROUTES.REGISTER,
    title: 'Register',
    requiresAuth: false,
  },
  [ROUTES.FORGOT_PASSWORD]: {
    path: ROUTES.FORGOT_PASSWORD,
    title: 'Forgot Password',
    requiresAuth: false,
  },
  [ROUTES.PASSWORD_RESET_SENT]: {
    path: ROUTES.PASSWORD_RESET_SENT,
    title: 'Check Your Email',
    requiresAuth: false,
  },
  [ROUTES.VERIFY_EMAIL]: {
    path: ROUTES.VERIFY_EMAIL,
    title: 'Verify Email',
    requiresAuth: false,
  },
  [ROUTES.HOME]: {
    path: ROUTES.HOME,
    title: 'Home',
    requiresAuth: true,
  },
  [ROUTES.MAP]: {
    path: ROUTES.MAP,
    title: 'Map',
    requiresAuth: true,
  },
  [ROUTES.LIBRARY]: {
    path: ROUTES.LIBRARY,
    title: 'Library',
    requiresAuth: true,
  },
  [ROUTES.LIBRARY_ITEM]: {
    path: ROUTES.LIBRARY_ITEM,
    title: 'Item Detail',
    requiresAuth: true,
  },
  [ROUTES.PROFILE]: {
    path: ROUTES.PROFILE,
    title: 'Profile',
    requiresAuth: true,
  },
  [ROUTES.DASHBOARD]: {
    path: ROUTES.DASHBOARD,
    title: 'Dashboards',
    requiresAuth: true,
  },
  [ROUTES.SETTINGS]: {
    path: ROUTES.SETTINGS,
    title: 'Settings',
    requiresAuth: true,
    requiredRoles: ['admin'],
  },
  [ROUTES.GEOCODING]: {
    path: ROUTES.GEOCODING,
    title: 'Geocoding',
    requiresAuth: true,
  },
  [ROUTES.GEOCODING_SESSIONS]: { path: ROUTES.GEOCODING_SESSIONS, title: 'Geocoding Sessions', requiresAuth: true },
  [ROUTES.GEOCODING_SESSION]: { path: ROUTES.GEOCODING_SESSION, title: 'Geocoding Results', requiresAuth: true },
  [ROUTES.SECURITY]: {
    path: ROUTES.SECURITY,
    title: 'Security',
    requiresAuth: true,
    requiredLicense: ['security'],
  },
}
